package editor

import common.dto.Direction
import common.dto.ElementType
import common.dto.Vec3d
import editor.EditorManager.currentState
import editor.EditorManager.objects
import editor.EditorManager.structure
import editor.EditorManager.takeAction
import editor.action.EditorAction
import editor.action.RemoveAction
import editor.action.actionStack
import editor.action.undoStack
import editor.structure.*
import editor.ui.EditorHtml
import ext.asVec3d
import ext.asVector3
import ext.changeMaterialTransparency
import kotlinx.browser.document
import kotlinx.html.dom.append
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.events.MouseEvent
import org.w3c.dom.events.WheelEvent
import raycasting.RaycastingManager
import rendering.CameraManager
import rendering.EffectManager
import rendering.RendererManager
import rendering.SceneManager
import three.OrbitControls
import three.js.AxesHelper
import three.js.Group
import util.DebugManager
import util.castedEventListener
import kotlin.math.sign

/**
 * ## Editor
 *
 * The idea of the TIMBERIX editing experience is as follows:
 *
 * First, the user is in the **HOVERING** state. There, they can hover over a structure element and
 * thereby "select" it. Additionally, a holographic preview of the to-be-placed element is shown.
 *
 * Then, when the user presses down the left mouse button, they enter the **PLACING** state. While
 * the mouse is pressed, the orbit controls are locked and the user can move the mouse to change
 * the position of the to-be-placed element.
 *
 * Both in the HOVERING and PLACING state, the position of the to-be-placed element depends
 * on the "offset". The offset is calculated by intersecting the cursor ray with
 * an invisible plane that is placed on the surface of the target element. For example,
 * when placing a brick on a side of a Brixi, the offset is used to move the brick along
 * on of the axes in relation to the Brixi. This way, the brick can be placed so that e.g.
 * the Brixi clamps to it further from the center of a side.
 *
 * After releasing the mouse button in the PLACING state, the element is actually added to
 * the structure and the user is back in the HOVERING state.
 */
// todo fix pos of brixi for XR; use ray from controller
object EditorManager {

    /**
     * World point of the intersection with the face of the target element. Persisted during the HOVERING state
     * and used to construct the plane in the PLACING state.
     */
    private var intersectionPoint: Vec3d? = null

    /**
     * The target element. Persisted during the HOVERING state and used in the PLACING state.
     */
    private var selectedElt: StructureElement? = null

    /**
     * During the PLACING state, the user may apply rotation to the to-be-placed element by scrolling the mouse wheel.
     * Scrolling once increments or decrements the rotation by 2.0 (or 1.0 when shift is pressed).
     *
     * This value is persisted during the PLACING state and reset when leaving it.
     *
     * @see [currentState]
     */
    private var currentScrollRotation = 0

    /**
     * The direction of the plane on the object that the cursor ray intersects, in the local space of the object.
     * Allows us to easily get the normal vector needed for creating the plane or calculating the position/rotation
     * of the placed element.
     */
    private var localIntersectionDirection: Direction? = null

    /**
     * The element that would be placed in the editor, when the user releases the mouse button. Before, it
     * functions as a preview.
     */
    // todo somehow cache placed element
    private var placedElement: StructureElement? = null
        set(value) {
            // transparent preview
            field?.object3D?.children?.firstOrNull()?.changeMaterialTransparency(1.0)
            value?.object3D?.children?.firstOrNull()?.changeMaterialTransparency(0.5)
            field = value
        }

    /**
     * Element type that should be placed next.
     */
    var toBePlacedType: ElementType = ElementType.Brixi
        set(value) {
            field = value
            EditorHtml.updatePlaceTypeInUi(value)
        }

    /**
     * @see EditorManager
     */
    private var currentState: EditorState = EditorState.HOVERING
        set(value) {
            field = value
            orbitControls.enabled = value.allowOrbit
            if (value == EditorState.HOVERING) {
                currentScrollRotation = 0
            }
        }

    /**
     * The group of THREE objects that are currently in the editor, excluding the preview etc.!
     *
     * This group should only be updated when something in the structure changes!
     * Currently, this is done by re-instantiating every [StructureElement] thing upon every yet-so-small change,
     * which can obviously be optimized further.
     */
    private val objects = Group().apply {
        add(*defaultStructure.instantiate())
    }

    /**
     * The group of THREE objects that are directly added to the scene, including the preview etc.!
     */
    private val group = Group().apply {
        add(objects)
    }

    /**
     * An abstraction layer for the current structure that is being built in the editor.
     *
     * Consists of [StructureElement]s.
     */
    private val structure: MutableStructure = defaultStructure.toMutableList()

    /**
     * Set the current structure to the specified [structure].
     */
    fun setStructure(structure: Structure) {
        this.structure.clear()
        this.structure.addAll(structure)
        onStructureUpdate()
    }

    /**
     * Return a read-only copy of the current structure.
     */
    fun getStructure() = structure.toList()

    /**
     * A visual aid that shows the orientation of the world axes.
     */
    val axesHelper = AxesHelper(500)

    /**
     * Allow the user to pan, zoom and rotate the camera. Only enabled in the HOVERING state.
     */
    private val orbitControls = OrbitControls(CameraManager.camera, RendererManager.renderer.domElement)

    /**
     * Add a listener for the specified [event] that is only active when the editor is in a certain state.
     */
    private inline fun <reified T : Event> addConditionalListener(
        state: EditorState,
        event: String,
        crossinline listener: (T) -> Unit
    ) {
        castedEventListener<T>(event) {
            if (currentState == state) listener(it)
        }
    }

    /**
     * Called once at the beginning to initialize the editor.
     * - add the html
     * - add the group which contains the structure
     * - add mousedown/-up listeners for switching between states
     */
    fun init() {
        document.body!!.append {
            EditorHtml.addHtmlTo(this)
        }
        SceneManager.scene.add(axesHelper)
        SceneManager.scene.add(group)
        EffectManager.init()

        addConditionalListener<MouseEvent>(EditorState.HOVERING, "mousedown") { event ->
            when (event.button.toInt()) {
                // left mouse button
                0 -> {
                    if (placedElement != null) currentState = EditorState.PLACING
                }
                // middle mouse button -> change type to type of selected element
                1 -> {
                    selectedElt?.let {
                        toBePlacedType = it.type
                    }
                }
            }
        }

        addConditionalListener<MouseEvent>(EditorState.PLACING, "mouseup") {
            if (placedElement == null) {
                println("WARNING !!! PLACED ELEMENT WAS NULL ON MOUSE UP IN PLACING !!!")
            } else {
                takeAction(EditorAction(after = placedElement!!))
            }
            currentState = EditorState.HOVERING
        }

        addConditionalListener<WheelEvent>(EditorState.PLACING, "wheel") {
            // we don't care about the specific delta, just the direction in which
            // the user scrolled
            if (it.shiftKey) {
                // fine-grained rotation
                currentScrollRotation += sign(it.deltaY).toInt()
            } else {
                // coarse-grained rotation
                currentScrollRotation += 2 * sign(it.deltaY).toInt()
                // make sure the rotation is a multiple of 2 again
                currentScrollRotation = currentScrollRotation / 2 * 2
            }
        }

        // Keybindings
        addConditionalListener<KeyboardEvent>(EditorState.HOVERING, "keydown") {
            if (it.ctrlKey) {
                if (it.key == "z") undo() else if (it.key == "y") redo()
            }
            if (it.key == "Delete" && structure.size > 1) {
                selectedElt?.let { elt -> takeAction(RemoveAction(elt)) }
            }
        }
    }

    /**
     * Called during the render loop when the user is in the HOVERING state.
     *
     * At this stage, the user did not yet decide what to do with the to-be-placed element, thus we
     * re-calculate stuff regarding the selected/target element.
     */
    private fun handleHovering() {
        // Update the placed and selected element
        val intersection = RaycastingManager.getFirstIntersection(objects.children)
        // persist the intersection point for the placing state
        intersectionPoint = intersection?.point?.asVec3d()
        val selected = SelectionManager.maybeSelect(objects.children, intersection)?.parent
        selectedElt = selected?.let {
            structure.firstOrNull { it.object3D.uuid == selected.uuid }
        }
        placedElement = selectedElt?.let {
            localIntersectionDirection =
                Direction.from(intersection?.face?.normal?.asVec3d() ?: return@let null) ?: return@let null
            PlaceManager.placeElement(
                target = it,
                localDirection = localIntersectionDirection!!,
                toBePlacedType = toBePlacedType,
                intersectionPoint = intersection.point,
                currentScrollRotation = currentScrollRotation
            )
        }
    }

    /**
     * Called during the render loop when the user is in the PLACING state.
     *
     * We construct a plane and intersect it with the cursor ray to allow the user to specify a larger offset.
     *
     * For the other values like the target element, we use the persisted values from the HOVERING state,
     * as the user can not change the target element while in PLACING.
     */
    private fun handlePlacing() {
        val plane = PlaneManager.constructIntersectionPlane(
            target = selectedElt ?: return,
            localNormal = localIntersectionDirection?.normal?.asVector3() ?: return,
            intersectionPoint = intersectionPoint?.asVector3() ?: return
        )
        val planeIntersection = RaycastingManager.intersectPlane(plane)
        placedElement = PlaceManager.placeElement(
            target = selectedElt!!,
            localDirection = localIntersectionDirection!!,
            toBePlacedType = toBePlacedType,
            intersectionPoint = planeIntersection,
            currentScrollRotation = currentScrollRotation
        )
    }

    /**
     * Called every frame to render the current structure, and additionally some kind of preview,
     * depending on the mouse position and state.
     */
    fun render() {
        when (currentState) {
            EditorState.HOVERING -> handleHovering()
            EditorState.PLACING -> handlePlacing()
        }

        placedElement?.let {
            DebugManager.debug("added element: ${it.type}")
            group.add(it.object3D)
        }

        DebugManager.debug("Action stack size: ${actionStack.size}")
        DebugManager.debug("scroll rotation: $currentScrollRotation")
        DebugManager.render()
        RendererManager.render()

        placedElement?.let {
            group.remove(it.object3D)
        }
    }

    /**
     * Update the three-js objects according to the current [structure].
     */
    private fun onStructureUpdate() {
        objects.clear()
        objects.add(*structure.instantiate())
    }

    /**
     * We assume something (in the [structure]) has changed due to this action and must update the
     * underlying [objects] accordingly.
     *
     * Clears the
     */
    private fun takeAction(action: EditorAction) {
        action.executeOn(structure)
        onStructureUpdate()
        actionStack += action

        undoStack.clear()
        updateUndoRedoButtons()
    }

    /**
     * Update the visibility of the undo and redo buttons in the ui based on the [actionStack] and [undoStack].
     *
     * Should be called when an action is taken and when undo/redo is called.
     */
    private fun updateUndoRedoButtons() {
        document.querySelector(".control-undo")?.classList?.toggle("disabled", actionStack.isEmpty())
        document.querySelector(".control-redo")?.classList?.toggle("disabled", undoStack.isEmpty())
    }

    /**
     * Undo and remove the last action from the [actionStack] and add it to the [undoStack].
     */
    fun undo() {
        actionStack.removeLastOrNull()?.let { lastAction ->
            lastAction.undoOn(structure)
            onStructureUpdate()
            undoStack += lastAction
        }
        updateUndoRedoButtons()
    }

    /**
     * Redo and remove the last action from the [undoStack] and add it to the [actionStack].
     *
     * In contrast to [takeAction], this does *not* clear the [undoStack]!
     */
    fun redo() {
        undoStack.removeLastOrNull()?.let { lastUndoneAction ->
            lastUndoneAction.executeOn(structure)
            onStructureUpdate()
            actionStack += lastUndoneAction
        }
        updateUndoRedoButtons()
    }
}